import React, { useState, useEffect } from 'react';
import {
  BrowserRouter as Router,
  Route,
  Routes,
  Navigate,
  useLocation,
  useNavigate,
  Link,
  Flex,
} from 'react-router-dom';
import {
  Layout,
  Menu,
  message,
  Spin,
  Divider,
  Grid,
  Drawer,
  Dropdown,
  Row,
  Col,
  Tooltip,
  Space,
  Tag,
  ConfigProvider,
  Avatar,
  Button,
} from 'antd';
import jaJP from 'antd/locale/ja_JP';
import { Helmet } from 'react-helmet';
import {
  FormOutlined,
  FireOutlined,
  DatabaseOutlined,
  UserOutlined,
  TeamOutlined,
  RocketOutlined,
  RobotOutlined,
  ClusterOutlined,
  SketchOutlined,
  MenuOutlined,
  CaretDownOutlined,
  ExportOutlined,
  DashboardOutlined,
  AccountBookOutlined,
  AuditOutlined,
  LockOutlined,
  SearchOutlined,
  FileTextOutlined,
  FormatPainterOutlined,
  FileSearchOutlined,
  TrophyOutlined,
  GiftOutlined,
  HomeOutlined,
  SettingOutlined,
  CreditCardOutlined,
  SwapOutlined,
  MailOutlined,
} from '@ant-design/icons';
import './main.css';
import './components/FactCheck/editor-custom.css';
import SEOArticleDashboard from './components/SEOArticle/Dashboard';
import SEOArticleGenerator from './components/SEOArticle/Generate';
import SEOArticleList from './components/SEOArticle/ArticleList';
import SEOArticleDetail from './components/SEOArticle/ArticleDetail';
import SharedArticle from './components/SEOArticle/SharedArticle';
import SharedFactCheck from './components/FactCheck/SharedFactCheck';
import SharedAnalysis from './components/AIO/Analysis/SharedAnalysis';
import UserSettings from './components/UserSettings';
import PlanSettings from './components/PlanSettings';
import BonusCreditPurchase from './components/BonusCreditPurchase';
import MyPage from './components/MyPage';
import Auth from './components/auth/Auth';
import RegisterProfile from './components/auth/RegisterProfile';
import ResetPassword from './components/auth/ResetPassword';
import RegisterEmail from './components/auth/RegisterEmail';
import Register from './components/auth/Register';
import RegisterThanks from './components/auth/RegisterThanks';
import { api, setAuthToken, recordActivity, creditsApi, trialApi } from './api';
import AuthenticatedMenu from './components/AuthenticatedMenu';
import AuthenticatedRoute from './components/AuthenticatedRoute';
import { getUserRole, getUserProfile } from './auth';
import UserList from './components/UserManagement/UserList';
import AdminDashboard from './components/AdminDashboard';
import AdminDashboardV2 from './components/AdminDashboardV2';
import AdminTransactions from './components/AdminTransactions';
import ConfirmEmail from './components/ConfirmEmail';
import RecommendedKeywords from './components/SEOArticle/RecommendedKeywords';
import SubscriptionSuccess from './components/SubscriptionSuccess';
import SubscriptionManagement from './components/SubscriptionManagement';
import SubscriptionCanceled from './components/SubscriptionCanceled';
import TermsOfService from './components/StaticPages/TermsOfService';
import PrivacyPolicy from './components/StaticPages/PrivacyPolicy';
import ContactUs from './components/StaticPages/ContactUs';
import ReserveMtg from './components/StaticPages/ReserveMtg';
import SupportPage from './components/StaticPages/SupportPage';
import AgreementManager from './components/AgreementManager';
import { setUserProperties } from './utils/ga4';
import FactCheckList from './components/FactCheck/FactCheckList';
import FactCheckDetail from './components/FactCheck/FactCheckDetail';
import FactCheckNew from './components/FactCheck/FactCheckNew';
// AIOコンポーネント
import AIODashboard from './components/AIO/Dashboard/AIODashboard';
import Analysis from './components/AIO/Analysis';
import AnalysisList from './components/AIO/Analysis/AnalysisList';
import AioGenerationWizard from './components/AIO/Generation';
import GenerationResultList from './components/AIO/Generation/ResultList';

// 管理画面コンポーネント
import ToppingManager from './components/AIO/Management/ToppingManagement';
import StyleManagement from './components/AIO/Management/StyleManagement';
import StyleNew from './components/AIO/Management/StyleNew';
import PromptManagement from './components/AIO/Management/PromptManagement';

// ゲーミフィケーションコンポーネント
import RankSystemPage from './components/Gamification/RankSystemPage';
import ReferralPage from './components/Gamification/ReferralPage';
import TutorialModal from './components/Gamification/TutorialModal';

// NEWバッジ機能
import { useNewFeatureBadge } from './hooks/useNewFeatureBadge';
import { NewTextBadge } from './components/Common/NewBadge';

// メールマーケティング（管理者用）
import EmailMarketing from './components/Admin/EmailMarketing';

// 登録モーダル（未ログイン体験用）
import WelcomePage from './components/Welcome/WelcomePage';

import ChannelTalkService from './services/ChannelService';
import Search from 'antd/es/transfer/search';
import { PushNotificationProvider } from './components/Common/PushNotificationProvider';

const { Header, Content, Sider, Footer } = Layout;
const { useBreakpoint } = Grid;

const menuOverlayStyle = {
  subMenuItemSelectedStyle: {
    backgroundColor: '#1890ff',
    color: 'white',
  },
};

const App = () => {
  const [isAuthenticated, setIsAuthenticated] = useState(false);
  const [userRole, setUserRole] = useState('user');
  const [userProfile, setUserProfile] = useState(null);
  // ticketCount は廃止（クレジット制に移行）
  const [creditInfo, setCreditInfo] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [trialEligible, setTrialEligible] = useState(false);
  const [trialBannerDismissed, setTrialBannerDismissed] = useState(false);
  const [sidebarVisible, setSidebarVisible] = useState(false);
  const [sidebarCollapsed, setSidebarCollapsed] = useState(true); // デフォルトで折りたたみ
  const [sidebarHovered, setSidebarHovered] = useState(false); // ホバー状態
  const [showTutorial, setShowTutorial] = useState(false);
  const [tutorialChecked, setTutorialChecked] = useState(false);
  const location = useLocation();
  const navigate = useNavigate();
  const screens = useBreakpoint();
  
  // NEWバッジ管理
  const { shouldShowBadgeForPath, markPathAsSeen } = useNewFeatureBadge();
  
  // パスが変わったらNEWバッジを消す
  useEffect(() => {
    markPathAsSeen(location.pathname);
  }, [location.pathname, markPathAsSeen]);

  const toggleSidebar = () => {
    setSidebarVisible(!sidebarVisible);
  };

  // チュートリアルステータスをチェック
  const checkTutorialStatus = async profile => {
    if (!profile) return;

    if (tutorialChecked) {
      return;
    }

    try {
      const { gamificationApi } = await import('./api');
      const status = await gamificationApi.getStatus();

      // survey_completedがtrueの場合、モーダルを表示しない
      // （アンケート送信済み = チュートリアルモーダルは完了扱い）
      if (status.survey_completed) {
        setTutorialChecked(true);
        return;
      }

      // survey_completedがfalseならチュートリアルを表示（ページ問わず）
      if (!status.survey_completed) {
        setShowTutorial(true);
      }

      setTutorialChecked(true);
    } catch (error) {
      console.error('Failed to check tutorial status:', error);
      setTutorialChecked(true);
    }
  };

  const handleTutorialComplete = () => {
    setShowTutorial(false);
    setTutorialChecked(true);
    // ダッシュボードツアーを開始するため、少し遅延してリロード
    // （アンケート送信のDB反映を待ってからリロードする）
    if (location.pathname.startsWith('/aio')) {
      setTimeout(() => {
        window.location.reload();
      }, 500);
    }
  };

  // ロケーション変更時にチュートリアルをチェック
  useEffect(() => {
    if (
      isAuthenticated &&
      userProfile &&
      !tutorialChecked
    ) {
      checkTutorialStatus(userProfile);
    }
  }, [location.pathname, isAuthenticated, userProfile]);

  // ResizeObserverのエラーを抑制
  useEffect(() => {
    const errorHandler = e => {
      if (
        e.message ===
          'ResizeObserver loop completed with undelivered notifications.' ||
        e.message.includes('ResizeObserver')
      ) {
        e.stopImmediatePropagation();
        const resizeObserverErrDiv = document.getElementById(
          'webpack-dev-server-client-overlay-div',
        );
        const resizeObserverErr = document.getElementById(
          'webpack-dev-server-client-overlay',
        );
        if (resizeObserverErr) {
          resizeObserverErr.setAttribute('style', 'display: none');
        }
        if (resizeObserverErrDiv) {
          resizeObserverErrDiv.setAttribute('style', 'display: none');
        }
      }
    };

    // より包括的なエラーハンドリング
    const originalError = console.error;
    console.error = (...args) => {
      if (typeof args[0] === 'string' && args[0].includes('ResizeObserver')) {
        return;
      }
      originalError.apply(console, args);
    };

    window.addEventListener('error', errorHandler);
    return () => {
      window.removeEventListener('error', errorHandler);
      console.error = originalError;
    };
  }, []);

  // グローバルUTM/紹介コードキャプチャ: どのページに着地してもlocalStorageに保存
  useEffect(() => {
    const params = new URLSearchParams(location.search);
    const utmKeys = ['utm_source', 'utm_medium', 'utm_campaign', 'utm_content', 'ref', 'fbclid'];
    const captured = {};
    utmKeys.forEach(key => {
      const val = params.get(key);
      if (val) captured[key] = val;
    });

    if (Object.keys(captured).length > 0) {
      const stored = JSON.parse(localStorage.getItem('utmParams') || '{}');
      const merged = { ...stored, ...captured };
      localStorage.setItem('utmParams', JSON.stringify(merged));
    }
  }, []);

  const getSelectedKey = pathname => {
    if (pathname === '/welcome') return '/aio/dashboard';
    if (pathname === '/seo-article') return '/seo-article';
    // AIOダッシュボード
    if (pathname === '/aio/dashboard') return '/aio/dashboard';
    if (pathname === '/aio' || pathname === '/aio/') return '/aio/dashboard';
    // 分析
    if (pathname === '/aio/analysis/new') return '/aio/analysis/new';
    if (pathname.startsWith('/aio/analysis/list')) return '/aio/analysis/list';
    // 分析詳細ページは一覧をカレントにする
    if (pathname.startsWith('/aio/analysis/')) return '/aio/analysis/list';
    if (pathname.startsWith('/aio/analysis')) return '/aio/analysis/new'; // その他の分析関連画面
    // 生成（v2）
    if (pathname === '/aio/generation/new') return '/aio/generation/new';
    if (pathname.startsWith('/aio/generation/list'))
      return '/aio/generation/list';
    if (pathname.startsWith('/aio/generation/')) return '/aio/generation/new';
    if (pathname.startsWith('/aio/generation')) return '/aio/generation/new';
    if (pathname.startsWith('/aio/style')) return '/aio/style';
    if (pathname === '/aio/original') return '/aio/original';
    if (pathname === '/rank-system') return '/rank-system';
    if (pathname === '/referral') return '/referral';
    if (pathname === '/mypage') return '/mypage';
    if (pathname === '/prompt-management') return '/prompt-management';
    if (pathname.startsWith('/seo-article/list')) return '/seo-article/list';
    if (pathname.startsWith('/seo-article/detail')) return '/seo-article/list';
    if (pathname.startsWith('/seo-article/observe_task'))
      return '/seo-article/observe_task';
    if (pathname.startsWith('/seo-article/keywords-recommend'))
      return '/seo-article/keywords-recommend';
    if (pathname === '/fact-check') return '/fact-check';
    if (pathname.startsWith('/fact-check/new')) return '/fact-check';
    if (pathname.startsWith('/fact-check/tasks')) return '/fact-check/tasks';
    if (pathname.startsWith('/fact-check/result')) return '/fact-check/result';
    if (pathname.startsWith('/admin/dashboard')) return '/admin/dashboard';
    if (pathname.startsWith('/admin/user-management'))
      return '/admin/user-management';
    if (pathname.startsWith('/admin/email-marketing'))
      return '/admin/email-marketing';
    if (pathname.startsWith('/admin/transactions'))
      return '/admin/transactions';
    if (pathname.startsWith('/plan')) return '/plan';
    if (pathname.startsWith('/bonus-credit')) return '/bonus-credit';
    if (pathname.startsWith('/settings')) return '/settings';
    if (pathname.startsWith('/subscription-'))
      return '/subscription-management';
    return '';
  };

  useEffect(() => {
    const checkAuth = async () => {
      setIsLoading(true);
      const token = localStorage.getItem('token');

      if (token) {
        setAuthToken(token);
        try {
          const role = await getUserRole();
          setUserRole(role);
          setIsAuthenticated(true);

          try {
            const profile = await getUserProfile();
            setUserProfile(profile);

            if (profile) {
              // チャネルトーク会員ユーザー設定
              ChannelTalkService.bootMember(profile);

              setUserProperties(profile.user_id, {
                role: profile.role,
                plan: profile.plan?.name,
                company: profile.company_name,
                // ticket_count は廃止（クレジット制に移行）
                fact_check_access: profile.early_access?.fact_check || false,
              });

              // チュートリアルステータスをチェック
              checkTutorialStatus(profile);

              // フリープランならトライアル権利チェック
              if (profile.plan?.type === 'free') {
                try {
                  const elig = await trialApi.checkEligibility();
                  setTrialEligible(elig.eligible);
                } catch (e) { /* ignore */ }
              }
            }
          } catch (error) {
            console.error('ユーザー情報の取得に失敗しました:', error);
            message.error('ユーザー情報の取得に失敗しました。');
          }
        } catch (error) {
          localStorage.removeItem('token');
          setIsAuthenticated(false);
          message.error('認証に失敗しました。再度ログインしてください。');
          if (!location.pathname.startsWith('/shared')) {
            redirectToLogin(location);
          }
        }
      } else {
        setIsAuthenticated(false);
        // 匿名ユーザー用チャネルトーク設定
        ChannelTalkService.bootAnonymous();
      }
      setIsLoading(false);
    };

    checkAuth();
  }, [location.pathname, location.search]);

  useEffect(() => {
    const token = localStorage.getItem('token');
    if (!token) return;

    recordActivity('login');

    const intervalId = setInterval(
      () => {
        recordActivity('login');
      },
      30 * 60 * 1000,
    );

    return () => clearInterval(intervalId);
  }, []);

  // SPA用ページトラッキング
  useEffect(() => {
    if (isAuthenticated) {
      ChannelTalkService.setPage(location.pathname);
    }
  }, [location.pathname, isAuthenticated]);

  const redirectToLogin = (location, isLogout = false) => {
    const currentParams = new URLSearchParams(location.search);
    const storedParams = JSON.parse(localStorage.getItem('utmParams') || '{}');

    // 現在のURLパラメータを優先し、ない場合はストレージのものを使用
    const combinedParams = new URLSearchParams({
      ...storedParams,
      ...Object.fromEntries(currentParams),
    });

    const utmKeys = [
      'utm_source',
      'utm_medium',
      'utm_campaign',
      'utm_content',
      'ref',
    ];
    const filteredParams = new URLSearchParams();

    // UTMパラメータのみを抽出
    for (const key of utmKeys) {
      const value = combinedParams.get(key);
      if (value) {
        filteredParams.append(key, value);
      }
    }

    const redirectUrl = `/login${filteredParams.toString() ? `?${filteredParams.toString()}` : ''}`;
    navigate(redirectUrl, { replace: true });
  };

  useEffect(() => {
    if (isAuthenticated && userProfile) {
      const wsProtocol = window.location.protocol === 'https:' ? 'wss:' : 'ws:';
      // const wsUrl = `${wsProtocol}//${window.location.hostname}:8000/ws/${userProfile.user_id}`;
      // const wsUrl = `${wsProtocol}//test-api.magicss.ai/ws/${userProfile.user_id}`;
      const wsUrl = `${wsProtocol}//api.magicss.ai/ws/${userProfile.user_id}`;

      const ws = new WebSocket(wsUrl);

      ws.onopen = () => {
        console.log('WebSocket connection established');
        const token = localStorage.getItem('token');

        // 少し待ってからトークン送信（ブラウザによる状態同期の遅延対応）
        setTimeout(() => {
          if (ws.readyState === WebSocket.OPEN) {
            ws.send(token);
          }
        }, 50);
      };

      ws.onmessage = event => {
        const data = JSON.parse(event.data);
        console.log('[DEBUG-WS] メッセージ受信:', data);

        // ticket_count の更新は廃止（クレジット制に移行）

        // メッセージ通知
        if (data.message) {
          message.success(data.message);
        }

        // タスク関連のメッセージ処理
        if (data.type) {
          // フラグメントメッセージの場合、必要なIDを設定
          if (data.type === 'new_fragment' && data.fragment) {
            // フラグメントIDを確実に設定
            data.fragment.id = `${data.task_id}-${data.fragment.order_index}`;

            // 配列の初期化を確実に行う
            if (!Array.isArray(data.fragment.reference_info)) {
              data.fragment.reference_info = [];
            }
            if (!Array.isArray(data.fragment.deep_check_sources)) {
              data.fragment.deep_check_sources = [];
            }
          }

          // window.factCheckWebSocketMessageが存在する場合のみ呼び出し
          if (typeof window.factCheckWebSocketMessage === 'function') {
            try {
              window.factCheckWebSocketMessage(data);
            } catch (error) {
              console.error('[DEBUG-WS] メッセージ処理エラー:', error);
            }
          }
        }
      };

      ws.onerror = error => {
        console.error('WebSocket error:', error);
      };

      ws.onclose = () => {
        console.log('WebSocket connection closed');
      };

      return () => {
        if (ws.readyState === WebSocket.OPEN) {
          ws.close();
        }
      };
    }
  }, [isAuthenticated, userProfile]);

  // クレジット情報取得
  useEffect(() => {
    const fetchCreditInfo = async () => {
      if (isAuthenticated) {
        try {
          const info = await creditsApi.getMyCredits();
          setCreditInfo(info);
        } catch (error) {
          console.error('Failed to fetch credit info:', error);
        }
      }
    };

    fetchCreditInfo();

    // 30秒ごとに更新
    const interval = setInterval(fetchCreditInfo, 30000);
    return () => clearInterval(interval);
  }, [isAuthenticated]);


  useEffect(() => {
    const recordActivity = async () => {
      if (isAuthenticated) {
        try {
          await api.post('/users/record-activity', { activity_type: 'login' });
        } catch (error) {
          console.error('Failed to record login activity:', error);
        }
      }
    };

    recordActivity();
  }, [isAuthenticated]);

  const modeMenu = (
    <Menu>
      <Menu.SubMenu
        key="seo-article"
        title="SEO記事生成"
        style={
          location.pathname.startsWith('/seo-article')
            ? { backgroundColor: '#e6f7ff', color: '#1890ff' }
            : {}
        }
        className={
          location.pathname.startsWith('/seo-article')
            ? 'current-menu-item'
            : ''
        }
        onTitleClick={() => navigate('/seo-article')}
        popupClassName="submenu-popup"
      >
        <Menu.Item
          key="seo-article-new"
          onClick={() => navigate('/seo-article')}
          style={
            location.pathname === '/seo-article'
              ? { backgroundColor: '#e6f7ff', color: '#1890ff' }
              : {}
          }
          className={
            location.pathname === '/seo-article' ? 'current-menu-item' : ''
          }
        >
          新規生成
        </Menu.Item>

        <Menu.Item
          key="seo-article-list"
          onClick={() => navigate('/seo-article/list')}
          style={
            location.pathname.startsWith('/seo-article/list') ||
            location.pathname.startsWith('/seo-article/detail')
              ? { backgroundColor: '#e6f7ff', color: '#1890ff' }
              : {}
          }
          className={
            location.pathname.startsWith('/seo-article/list') ||
            location.pathname.startsWith('/seo-article/detail')
              ? 'current-menu-item'
              : ''
          }
        >
          生成結果一覧
        </Menu.Item>
        {(userRole === 'master' || userRole === 'admin') && (
          <Menu.Item
            key="seo-article-observe"
            onClick={() => navigate('/seo-article/observe_task')}
            style={
              location.pathname.startsWith('/seo-article/observe_task')
                ? { backgroundColor: '#e6f7ff', color: '#1890ff' }
                : {}
            }
            className={
              location.pathname.startsWith('/seo-article/observe_task')
                ? 'current-menu-item'
                : ''
            }
          >
            タスク監視
          </Menu.Item>
        )}
      </Menu.SubMenu>
      <Menu.SubMenu
        key="aio"
        title={
          <Space>
            AIOコンテンツモード
            <Tag color="red" style={{ fontSize: '10px', padding: '0 4px' }}>
              NEW
            </Tag>
          </Space>
        }
        style={
          location.pathname.startsWith('/aio')
            ? { backgroundColor: '#e6f7ff', color: '#1890ff' }
            : {}
        }
        className={
          location.pathname.startsWith('/aio') ? 'current-menu-item' : ''
        }
        onTitleClick={() => navigate('/aio/dashboard')}
        popupClassName="submenu-popup"
      >
        <Menu.Item
          key="aio-dashboard"
          onClick={() => navigate('/aio/dashboard')}
          style={
            location.pathname === '/aio/dashboard'
              ? { backgroundColor: '#e6f7ff', color: '#1890ff' }
              : {}
          }
          className={
            location.pathname === '/aio/dashboard' ? 'current-menu-item' : ''
          }
        >
          ダッシュボード
        </Menu.Item>
        <Menu.Item
          key="aio-analysis-new"
          onClick={() => navigate('/aio/analysis/new')}
          style={
            location.pathname === '/aio/analysis/new'
              ? { backgroundColor: '#e6f7ff', color: '#1890ff' }
              : {}
          }
          className={
            location.pathname === '/aio/analysis/new' ? 'current-menu-item' : ''
          }
        >
          新規リサーチ
        </Menu.Item>
        <Menu.Item
          key="aio-analysis-list"
          onClick={() => navigate('/aio/analysis/list')}
          style={
            location.pathname.startsWith('/aio/analysis/list')
              ? { backgroundColor: '#e6f7ff', color: '#1890ff' }
              : {}
          }
          className={
            location.pathname.startsWith('/aio/analysis/list')
              ? 'current-menu-item'
              : ''
          }
        >
          リサーチ結果
        </Menu.Item>
        <Menu.Item
          key="aio-generation-new"
          onClick={() => navigate('/aio/generation/new')}
          style={
            location.pathname === '/aio/generation/new'
              ? { backgroundColor: '#e6f7ff', color: '#1890ff' }
              : {}
          }
          className={
            location.pathname === '/aio/generation/new'
              ? 'current-menu-item'
              : ''
          }
        >
          コンテンツ生成
        </Menu.Item>
        <Menu.Item
          key="aio-generation-list"
          onClick={() => navigate('/aio/generation/list')}
          style={
            location.pathname.startsWith('/aio/generation/list')
              ? { backgroundColor: '#e6f7ff', color: '#1890ff' }
              : {}
          }
          className={
            location.pathname.startsWith('/aio/generation/list')
              ? 'current-menu-item'
              : ''
          }
        >
          生成結果一覧
        </Menu.Item>
        {userProfile ? (
          <>
            <Menu.Item
              key="aio-style"
              onClick={() => navigate('/aio/style')}
              style={
                location.pathname === '/aio/style'
                  ? { backgroundColor: '#e6f7ff', color: '#1890ff' }
                  : {}
              }
              className={
                location.pathname === '/aio/style' ? 'current-menu-item' : ''
              }
            >
              スタイル管理
            </Menu.Item>
            <Menu.Item
              key="aio-original"
              onClick={() => navigate('/aio/original')}
              style={
                location.pathname === '/aio/original'
                  ? { backgroundColor: '#e6f7ff', color: '#1890ff' }
                  : {}
              }
              className={
                location.pathname === '/aio/original' ? 'current-menu-item' : ''
              }
            >
              独自情報管理
            </Menu.Item>
          </>
        ) : (
          <>
            <Menu.Item key="aio-style" disabled>
              スタイル管理 🔒
            </Menu.Item>
            <Menu.Item key="aio-original" disabled>
              独自情報管理 🔒
            </Menu.Item>
          </>
        )}
      </Menu.SubMenu>
      {userProfile?.early_access?.fact_check ? (
        <Menu.SubMenu
          key="fact-check"
          title="ファクトチェック"
          style={
            location.pathname.startsWith('/fact-check')
              ? { backgroundColor: '#e6f7ff', color: '#1890ff' }
              : {}
          }
          className={
            location.pathname.startsWith('/fact-check')
              ? 'current-menu-item'
              : ''
          }
          onTitleClick={() => navigate('/fact-check')}
          popupClassName="submenu-popup"
        >
          <Menu.Item
            key="fact-check-new"
            onClick={() => navigate('/fact-check')}
            style={
              location.pathname === '/fact-check' ||
              location.pathname.startsWith('/fact-check/new')
                ? { backgroundColor: '#e6f7ff', color: '#1890ff' }
                : {}
            }
            className={
              location.pathname === '/fact-check' ||
              location.pathname.startsWith('/fact-check/new')
                ? 'current-menu-item'
                : ''
            }
          >
            新規タスク予約
          </Menu.Item>
          <Menu.Item
            key="fact-check-tasks"
            onClick={() => navigate('/fact-check/tasks')}
            style={
              location.pathname.startsWith('/fact-check/tasks') ||
              location.pathname.startsWith('/fact-check/result')
                ? { backgroundColor: '#e6f7ff', color: '#1890ff' }
                : {}
            }
            className={
              location.pathname.startsWith('/fact-check/tasks') ||
              location.pathname.startsWith('/fact-check/result')
                ? 'current-menu-item'
                : ''
            }
          >
            タスク一覧
          </Menu.Item>
        </Menu.SubMenu>
      ) : (
        <Menu.Item key="fact-check" disabled>
          <Tooltip title="一部のユーザー様にのみ試験提供中です。ご希望の場合はお問い合わせください。">
            <Space>
              <LockOutlined />
              ファクトチェック
            </Space>
          </Tooltip>
        </Menu.Item>
      )}
      <Menu.SubMenu
        key="user-settings"
        title="ユーザー設定"
        style={
          ['/settings', '/plan', '/bonus-credit', '/subscription-management'].some(
            path => location.pathname === path,
          )
            ? { backgroundColor: '#e6f7ff', color: '#1890ff' }
            : {}
        }
        className={
          ['/settings', '/plan', '/bonus-credit', '/subscription-management'].some(
            path => location.pathname === path,
          )
            ? 'current-menu-item'
            : ''
        }
        onTitleClick={() => navigate('/subscription-management')}
        popupClassName="submenu-popup"
      >
        <Menu.Item
          key="subscription-management"
          onClick={() => navigate('/subscription-management')}
          style={
            location.pathname === '/subscription-management' ||
            location.pathname === '/subscription-success' ||
            location.pathname === '/subscription-canceled'
              ? { backgroundColor: '#e6f7ff', color: '#1890ff' }
              : {}
          }
          className={
            location.pathname === '/subscription-management' ||
            location.pathname === '/subscription-success' ||
            location.pathname === '/subscription-canceled'
              ? 'current-menu-item'
              : ''
          }
        >
          サブスクリプション管理
        </Menu.Item>
        <Menu.Item
          key="plan-settings"
          onClick={() => navigate('/plan')}
          style={
            location.pathname === '/plan'
              ? { backgroundColor: '#e6f7ff', color: '#1890ff' }
              : {}
          }
          className={location.pathname === '/plan' ? 'current-menu-item' : ''}
        >
          プラン変更
        </Menu.Item>
        <Menu.Item
          key="bonus-credit"
          onClick={() => navigate('/bonus-credit')}
          style={
            location.pathname === '/bonus-credit'
              ? { backgroundColor: '#e6f7ff', color: '#1890ff' }
              : {}
          }
          className={location.pathname === '/bonus-credit' ? 'current-menu-item' : ''}
        >
          ボーナスクレジット購入
        </Menu.Item>
        <Menu.Item
          key="user-settings"
          onClick={() => navigate('/settings')}
          style={
            location.pathname === '/settings'
              ? { backgroundColor: '#e6f7ff', color: '#1890ff' }
              : {}
          }
          className={
            location.pathname === '/settings' ? 'current-menu-item' : ''
          }
        >
          登録情報変更
        </Menu.Item>
      </Menu.SubMenu>
      {(userRole === 'admin' || userRole === 'master') && (
        <Menu.SubMenu
          key="admin-menu"
          title="管理者用メニュー"
          style={
            location.pathname.startsWith('/admin')
              ? { backgroundColor: '#e6f7ff', color: '#1890ff' }
              : {}
          }
          className={
            location.pathname.startsWith('/admin') ? 'current-menu-item' : ''
          }
          onTitleClick={() => navigate('/admin/dashboard')}
          popupClassName="submenu-popup"
        >
          <Menu.Item
            key="admin-dashboard"
            onClick={() => navigate('/admin/dashboard')}
            style={
              location.pathname === '/admin/dashboard'
                ? { backgroundColor: '#e6f7ff', color: '#1890ff' }
                : {}
            }
            className={
              location.pathname === '/admin/dashboard'
                ? 'current-menu-item'
                : ''
            }
          >
            ダッシュボード
          </Menu.Item>
          <Menu.Item
            key="admin-user-management"
            onClick={() => navigate('/admin/user-management')}
            style={
              location.pathname === '/admin/user-management'
                ? { backgroundColor: '#e6f7ff', color: '#1890ff' }
                : {}
            }
            className={
              location.pathname === '/admin/user-management'
                ? 'current-menu-item'
                : ''
            }
          >
            ユーザー管理
          </Menu.Item>
          <Menu.Item
            key="admin-transactions"
            onClick={() => navigate('/admin/transactions')}
            style={
              location.pathname === '/admin/transactions'
                ? { backgroundColor: '#e6f7ff', color: '#1890ff' }
                : {}
            }
            className={
              location.pathname === '/admin/transactions'
                ? 'current-menu-item'
                : ''
            }
          >
            トランザクション一覧
          </Menu.Item>
        </Menu.SubMenu>
      )}
    </Menu>
  );

  const renderSidebar = () => {
    const selectedMenu = getSelectedKey(location.pathname);
    const effectiveCollapsed = sidebarCollapsed && !sidebarHovered;
    let sidebarContent;

    const currentModeLabel = (() => {
      if (selectedMenu.startsWith('/aio')) return 'AIOコンテンツ';
      if (selectedMenu.startsWith('/fact-check')) return 'ファクトチェック';
      if (selectedMenu.startsWith('/seo-article')) return 'SEO記事生成';
      if (selectedMenu === '/mypage' || selectedMenu === '/settings' || selectedMenu === '/plan' || selectedMenu === '/rank-system' || selectedMenu === '/referral' || selectedMenu === '/bonus-credit' || selectedMenu === '/subscription-management') return 'マイページ';
      return null;
    })();

    const modeSwitcher = currentModeLabel ? (
      <div style={{ padding: '10px 0 6px', borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <div
          style={{
            textAlign: 'center',
            color: 'rgba(255,255,255,0.7)', fontSize: 11, fontWeight: 500,
            letterSpacing: '0.05em',
            opacity: effectiveCollapsed ? 0 : 1,
            transition: 'opacity 0.2s cubic-bezier(0.645, 0.045, 0.355, 1)',
            whiteSpace: 'nowrap',
            overflow: 'hidden',
          }}
        >
          {currentModeLabel}
        </div>
      </div>
    ) : null;

    const createSidebarContent = (title, menuItems) => (
      <>
        <div
          style={{
            position: 'sticky',
            top: 36,
            height: 'calc(100vh - 36px)',
            overflow: 'auto',
          }}
        >
          {modeSwitcher}
          <Menu
            mode="inline"
            selectedKeys={[selectedMenu]}
            style={{
              borderRight: 0,
              paddingTop: '8px',
              background: 'transparent',
              color: '#fff',
            }}
          >
            {menuItems}
          </Menu>
        </div>
      </>
    );

    if (selectedMenu.startsWith('/seo-article')) {
      sidebarContent = createSidebarContent(
        <>
          <RobotOutlined style={{ marginRight: '4px' }} />
          SEO記事生成
        </>,
        <>
          <Menu.Item key="/seo-article" icon={<FormOutlined />}>
            <Link to="/seo-article">新規生成</Link>
          </Menu.Item>

          <Menu.Item key="/seo-article/list" icon={<DatabaseOutlined />}>
            <Link to="/seo-article/list">生成結果一覧</Link>
          </Menu.Item>
          {(userRole === 'master' || userRole === 'admin') && (
            <>
              <Divider style={{ margin: '16px 0' }} />
              <Menu.Item
                key="/seo-article/observe_task"
                icon={<ClusterOutlined />}
              >
                <Link to="/seo-article/observe_task">タスク監視</Link>
              </Menu.Item>
            </>
          )}
        </>,
      );
    } else if (selectedMenu.startsWith('/aio')) {
      sidebarContent = createSidebarContent(
        <>
          <RobotOutlined style={{ marginRight: '4px' }} />
          AIOコンテンツモード
        </>,
        <>
          {/* ダッシュボード / ウェルカム */}
          <Menu.Item key="/aio/dashboard" icon={<DashboardOutlined />}>
            <Link to={userProfile ? "/aio/dashboard" : "/welcome"}>
              {userProfile ? 'ダッシュボード' : 'magicssへようこそ'}
            </Link>
          </Menu.Item>

          {/* SEO/AIOリサーチ */}
          <div
            className={`sidebar-section-divider ${effectiveCollapsed ? 'collapsed' : ''}`}
          >
            <span className="divider-text">SEO/AIOリサーチ</span>
          </div>
          <Menu.Item key="/aio/analysis/new" icon={<SearchOutlined />}>
            <Link to="/aio/analysis/new">新規リサーチ</Link>
          </Menu.Item>
          <Menu.Item key="/aio/analysis/list" icon={<FileSearchOutlined />}>
            <Link to="/aio/analysis/list">リサーチ結果</Link>
          </Menu.Item>

          {/* コンテンツ生成 */}
          <div
            className={`sidebar-section-divider ${effectiveCollapsed ? 'collapsed' : ''}`}
          >
            <span className="divider-text">コンテンツ生成</span>
          </div>
          <Menu.Item key="/aio/generation/new" icon={<FormOutlined />}>
            <Link to="/aio/generation/new">新規生成</Link>
          </Menu.Item>
          <Menu.Item key="/aio/generation/list" icon={<DatabaseOutlined />}>
            <Link to="/aio/generation/list">生成結果一覧</Link>
          </Menu.Item>

          {/* カスタマイズ */}
          <div
            className={`sidebar-section-divider ${effectiveCollapsed ? 'collapsed' : ''}`}
          >
            <span className="divider-text">カスタマイズ</span>
          </div>
          {userProfile ? (
            <Menu.Item key="/aio/style" icon={<FormatPainterOutlined />}>
              <Link to="/aio/style">
                スタイル管理
                <NewTextBadge show={shouldShowBadgeForPath('/aio/style')} />
              </Link>
            </Menu.Item>
          ) : (
            <Tooltip title="ログインが必要です" placement="right">
              <Menu.Item key="/aio/style" icon={<LockOutlined />} disabled style={{ opacity: 0.5 }}>
                スタイル管理
              </Menu.Item>
            </Tooltip>
          )}
          {userProfile ? (
            <Menu.Item key="/aio/original" icon={<SketchOutlined />}>
              <Link to="/aio/original">
                独自情報管理
                <NewTextBadge show={shouldShowBadgeForPath('/aio/original')} />
              </Link>
            </Menu.Item>
          ) : (
            <Tooltip title="ログインが必要です" placement="right">
              <Menu.Item key="/aio/original" icon={<LockOutlined />} disabled style={{ opacity: 0.5 }}>
                独自情報管理
              </Menu.Item>
            </Tooltip>
          )}
        </>,
      );
    } else if (selectedMenu.startsWith('/fact-check')) {
      sidebarContent = createSidebarContent(
        <>
          <AuditOutlined style={{ marginRight: '4px' }} />
          ファクトチェック
        </>,
        <>
          <Menu.Item key="/fact-check" icon={<FormOutlined />}>
            <Link to="/fact-check">新規タスク予約</Link>
          </Menu.Item>
          <Menu.Item key="/fact-check/tasks" icon={<DatabaseOutlined />}>
            <Link to="/fact-check/tasks">タスク一覧</Link>
          </Menu.Item>
        </>,
      );
    } else if (
      selectedMenu === '/settings' ||
      selectedMenu === '/plan' ||
      selectedMenu === '/bonus-credit' ||
      selectedMenu === '/subscription-management' ||
      selectedMenu === '/mypage' ||
      selectedMenu === '/rank-system' ||
      selectedMenu === '/referral'
    ) {
      sidebarContent = createSidebarContent(
        'マイページ',
        <>
          <Menu.Item key="/mypage" icon={<HomeOutlined />}>
            <Link to="/mypage">マイページ</Link>
          </Menu.Item>
          <div
            className={`sidebar-section-divider ${effectiveCollapsed ? 'collapsed' : ''}`}
          >
            <span className="divider-text">サブスクリプション</span>
          </div>
          <Menu.Item
            key="/subscription-management"
            icon={<CreditCardOutlined />}
          >
            <Link to="/subscription-management">サブスクリプション管理</Link>
          </Menu.Item>
          <Menu.Item key="/plan" icon={<SwapOutlined />}>
            <Link to="/plan">プラン変更</Link>
          </Menu.Item>
          <Menu.Item key="/bonus-credit" icon={<FireOutlined />}>
            <Link to="/bonus-credit">ボーナスクレジット購入</Link>
          </Menu.Item>
          <div
            className={`sidebar-section-divider ${effectiveCollapsed ? 'collapsed' : ''}`}
          >
            <span className="divider-text">ランク・紹介</span>
          </div>
          <Menu.Item key="/rank-system" icon={<TrophyOutlined />}>
            <Link to="/rank-system">ランクシステム</Link>
          </Menu.Item>
          <Menu.Item key="/referral" icon={<GiftOutlined />}>
            <Link to="/referral">友達紹介</Link>
          </Menu.Item>
          <div
            className={`sidebar-section-divider ${effectiveCollapsed ? 'collapsed' : ''}`}
          >
            <span className="divider-text">アカウント</span>
          </div>
          <Menu.Item key="/settings" icon={<SettingOutlined />}>
            <Link to="/settings">登録情報変更</Link>
          </Menu.Item>
        </>,
      );
    } else if (
      (selectedMenu === '/admin/user-management' ||
        selectedMenu === '/admin/transactions' ||
        selectedMenu === '/admin/dashboard' ||
        selectedMenu === '/admin/email-marketing' ||
        selectedMenu === '/prompt-management') &&
      (userRole === 'admin' || userRole === 'master')
    ) {
      sidebarContent = createSidebarContent(
        '管理者メニュー',
        <>
          <div
            className={`sidebar-section-divider ${effectiveCollapsed ? 'collapsed' : ''}`}
          >
            <span className="divider-text">カスタマーサクセス</span>
          </div>
          <Menu.Item key="/admin/user-management" icon={<TeamOutlined />}>
            <Link to="/admin/user-management">ユーザー管理</Link>
          </Menu.Item>
          <Menu.Item key="/admin/email-marketing" icon={<MailOutlined />}>
            <Link to="/admin/email-marketing">メール配信</Link>
          </Menu.Item>
          <div
            className={`sidebar-section-divider ${effectiveCollapsed ? 'collapsed' : ''}`}
          >
            <span className="divider-text">その他</span>
          </div>
          <Menu.Item key="/admin/transactions" icon={<DatabaseOutlined />}>
            <Link to="/admin/transactions">トランザクション一覧</Link>
          </Menu.Item>
          {userRole === 'master' && (
            <Menu.Item key="/prompt-management" icon={<LockOutlined />}>
              <Link to="/prompt-management">プロンプト管理</Link>
            </Menu.Item>
          )}
        </>,
      );
    }

    const sidebarProps = {
      width: 250,
      collapsedWidth: 73,
      collapsed: effectiveCollapsed,
      onMouseEnter: () => setSidebarHovered(true),
      onMouseLeave: () => setSidebarHovered(false),
      style: {
        minHeight: '100vh',
        background: 'linear-gradient(135deg, #0072ff 0%, #00d4ff 100%)',
        overflow: 'visible',
      },
      theme: 'dark',
    };

    return screens.xl ? (
      <Sider {...sidebarProps}>{sidebarContent}</Sider>
    ) : (
      <Drawer
        placement="left"
        closable={false}
        onClose={toggleSidebar}
        visible={sidebarVisible}
        styles={{ body: { padding: 0, background: '#fff' } }}
        width={280}
      >
        <div className="drawer-menu-content" style={{ 
          background: '#fff',
          minHeight: '100%'
        }}>
          {/* モード切替メニュー（アコーディオン式） */}
          <Menu
            mode="inline"
            className="drawer-compact-menu"
            defaultOpenKeys={[
              location.pathname.startsWith('/seo-article') ? 'seo-article' :
              location.pathname.startsWith('/aio') ? 'aio' :
              location.pathname.startsWith('/fc') ? 'fc' : ''
            ]}
            selectedKeys={[location.pathname]}
            style={{ border: 'none', fontSize: 13 }}
            onClick={() => setSidebarVisible(false)}
            items={[
              {
                key: 'aio',
                label: <span>AIOコンテンツモード <Tag color="red" style={{ fontSize: 10, padding: '0 4px' }}>NEW</Tag></span>,
                children: [
                  // ホーム
                  { type: 'group', label: <span style={{ fontSize: 10, color: '#999' }}>ホーム</span> },
                  { key: '/aio/dashboard', label: userProfile ? 'ダッシュボード' : 'magicssへようこそ', onClick: () => navigate(userProfile ? '/aio/dashboard' : '/welcome') },
                  // リサーチ
                  { type: 'group', label: <span style={{ fontSize: 10, color: '#999' }}>リサーチ</span> },
                  { key: '/aio/analysis/new', label: '新規リサーチ', onClick: () => navigate('/aio/analysis/new') },
                  { key: '/aio/analysis/list', label: 'リサーチ結果', onClick: () => navigate('/aio/analysis/list') },
                  // コンテンツ生成
                  { type: 'group', label: <span style={{ fontSize: 10, color: '#999' }}>コンテンツ生成</span> },
                  { key: '/aio/generation/new', label: '新規生成', onClick: () => navigate('/aio/generation/new') },
                  { key: '/aio/generation/list', label: '生成結果一覧', onClick: () => navigate('/aio/generation/list') },
                  // カスタマイズ
                  { type: 'group', label: <span style={{ fontSize: 10, color: '#999' }}>カスタマイズ</span> },
                  ...(userProfile
                    ? [
                        { key: '/aio/style', label: 'スタイル管理', onClick: () => navigate('/aio/style') },
                        { key: '/aio/original', label: '独自情報管理', onClick: () => navigate('/aio/original') },
                      ]
                    : [
                        { key: '/aio/style', label: 'スタイル管理 🔒', disabled: true },
                        { key: '/aio/original', label: '独自情報管理 🔒', disabled: true },
                      ]
                  ),
                ],
              },
              ...(userProfile?.early_access?.fact_check ? [{
                key: 'fc',
                label: 'ファクトチェック',
                children: [
                  { key: '/fact-check', label: '新規タスク予約', onClick: () => navigate('/fact-check') },
                  { key: '/fact-check/tasks', label: 'タスク一覧', onClick: () => navigate('/fact-check/tasks') },
                ],
              }] : []),
              ...(userProfile ? [
                { type: 'divider' },
                { type: 'group', label: <span style={{ fontSize: 10, color: '#999' }}>アカウント</span> },
                {
                  key: 'user-settings',
                  label: 'ユーザー設定',
                  children: [
                    { key: '/mypage', label: 'マイページ', onClick: () => navigate('/mypage') },
                    { key: '/subscription-management', label: 'サブスクリプション管理', onClick: () => navigate('/subscription-management') },
                    { key: '/plan', label: 'プラン変更', onClick: () => navigate('/plan') },
                    { key: '/bonus-credit', label: 'ボーナスクレジット購入', onClick: () => navigate('/bonus-credit') },
                    { key: '/settings', label: '登録情報変更', onClick: () => navigate('/settings') },
                    { key: '/rank-system', label: 'ランクシステム', onClick: () => navigate('/rank-system') },
                    { key: '/referral', label: '友だち紹介', onClick: () => navigate('/referral') },
                  ],
                },
                ...((userRole === 'admin' || userRole === 'master') ? [{
                  key: 'admin-menu',
                  label: '管理者用メニュー',
                  children: [
                    { key: '/admin/dashboard', label: 'ダッシュボード', onClick: () => navigate('/admin/dashboard') },
                    { key: '/admin/user-management', label: 'ユーザー管理', onClick: () => navigate('/admin/user-management') },
                    { key: '/admin/email-marketing', label: 'メール配信', onClick: () => navigate('/admin/email-marketing') },
                    { key: '/admin/transactions', label: 'トランザクション一覧', onClick: () => navigate('/admin/transactions') },
                    { key: '/prompt-management', label: 'プロンプト管理', onClick: () => navigate('/prompt-management') },
                  ],
                }] : []),
                { type: 'group', label: <span style={{ fontSize: 11, color: '#999', padding: '8px 0 4px' }}>その他</span> },
                {
                  key: 'seo-article',
                  label: <span style={{ color: '#999' }}>SEO記事生成 [Legacy]</span>,
                  children: [
                    { key: '/seo-article', label: '新規生成', onClick: () => navigate('/seo-article') },
                    { key: '/seo-article/list', label: '生成結果一覧', onClick: () => navigate('/seo-article/list') },
                  ],
                },
              ] : []),
            ]}
          />
        </div>
      </Drawer>
    );
  };

  const renderAuthenticatedRoutes = () => {
    const effectiveCollapsed = sidebarCollapsed && !sidebarHovered;
    const sidebarWidth = effectiveCollapsed ? 73 : 250;

    return (
      <Layout hasSider style={{ minHeight: '100vh' }}>
        {renderSidebar()}
        <Layout>
          <Header
            style={{
              background: '#fff',
              padding: screens.xl ? '0 24px' : '0 12px',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
              boxShadow: '0 2px 8px rgba(0,0,0,0.06)',
              position: 'relative',
              height: screens.xl ? 64 : 56,
            }}
          >
            {/* モバイル: 左側にハンバーガーメニュー（Drawerを開く） */}
            {!screens.xl && (
              <MenuOutlined
                className="menu-icon"
                style={{ cursor: 'pointer', fontSize: 20 }}
                onClick={toggleSidebar}
              />
            )}
            {/* モバイル: 中央にロゴ (タグラインなし、magicss + v2のみ) */}
            {!screens.xl && (
              <div
                style={{
                  position: 'absolute',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  display: 'flex',
                  alignItems: 'flex-end',
                  gap: '2px',
                }}
              >
                <span className="logo header-logo" style={{ fontSize: 22 }}>
                  magicss
                </span>
                <span
                  className="logo version-badge"
                  style={{ lineHeight: '1.1', paddingBottom: '12px', fontSize: 10 }}
                >
                  v2
                </span>
              </div>
            )}
            {/* PC: 中央にロゴを配置（サイドバーの幅を考慮） */}
            {screens.xl && (
              <div
                style={{
                  position: 'absolute',
                  left: '50%',
                  transform: 'translateX(-50%)',
                  zIndex: 1,
                  display: 'flex',
                  alignItems: 'flex-end',
                  gap: '3px',
                  paddingBottom: '2px',
                }}
              >
                <div className="logo header-logo">
                  <span>Ignite creativity with..</span>magicss
                </div>
                <span
                  className="logo version-badge"
                  style={{ lineHeight: '1.1', paddingBottom: '18px' }}
                >
                  v2
                </span>
              </div>
            )}
            {/* PC: 認証メニュー or ゲスト用ボタン */}
            {screens.xl && (
              userProfile ? (
                <AuthenticatedMenu
                  userProfile={userProfile}
                  userRole={userRole}
                  isMobile={!screens.xl}
                  location={location}
                />
              ) : (
                <Space style={{ marginLeft: 'auto' }}>
                  <Button type="primary" onClick={() => navigate('/register')}
                    style={{ background: 'linear-gradient(90deg, #0072ff, #00d4ff)', border: 'none', fontWeight: 600 }}>
                    無料で始める
                  </Button>
                  <Button onClick={() => navigate('/login')}>
                    ログイン
                  </Button>
                </Space>
              )
            )}
            {/* モバイル: 右側 ユーザーメニュー or ゲスト用ボタン */}
            {!screens.xl && (
              userProfile ? (
                <Dropdown
                  menu={{ items: [
                    { key: 'credit-header', label: (
                      <div style={{ padding: '4px 0', borderBottom: '1px solid #f0f0f0', marginBottom: 4 }}>
                        <Space>
                          <FireOutlined style={{ color: '#ff6b35' }} />
                          <span style={{ fontWeight: 500 }}>
                            クレジット: {creditInfo?.available_regular?.toLocaleString() || 0}
                            {creditInfo?.bonus_credit > 0 && ` (+${creditInfo.bonus_credit.toLocaleString()})`}
                          </span>
                        </Space>
                      </div>
                    ), disabled: true },
                    { type: 'divider' },
                    { key: 'mypage', label: 'マイページ', icon: <HomeOutlined />, onClick: () => navigate('/mypage') },
                    { key: 'subscription', label: 'サブスクリプション管理', icon: <CreditCardOutlined />, onClick: () => navigate('/subscription-management') },
                    { key: 'plan', label: 'プラン変更', icon: <SwapOutlined />, onClick: () => navigate('/plan') },
                    { key: 'bonus', label: 'ボーナスクレジット購入', icon: <GiftOutlined />, onClick: () => navigate('/bonus-credit') },
                    { type: 'divider' },
                    { key: 'rank', label: 'ランクシステム', icon: <TrophyOutlined />, onClick: () => navigate('/rank-system') },
                    { key: 'referral', label: '紹介', icon: <TeamOutlined />, onClick: () => navigate('/referral') },
                    { key: 'settings', label: '登録情報の変更', icon: <SettingOutlined />, onClick: () => navigate('/settings') },
                    { type: 'divider' },
                    { key: 'logout', label: 'ログアウト', danger: true, onClick: () => {
                      localStorage.removeItem('token');
                      window.location.href = '/login';
                    }},
                  ]}}
                  trigger={['click']}
                  placement="bottomRight"
                >
                  <Avatar
                    size={28}
                    style={{ backgroundColor: '#00d4ff', cursor: 'pointer' }}
                    icon={<UserOutlined />}
                  />
                </Dropdown>
              ) : (
                <Button type="primary" size="small" onClick={() => navigate('/register')}>
                  無料で始める
                </Button>
              )
            )}
          </Header>
          {/* トライアル訴求バナー: ヘッダー直下（ゲスト or フリープラン+権利あり） */}
          {((!userProfile && !location.pathname.startsWith('/register') && !location.pathname.startsWith('/login')) ||
            (userProfile?.plan?.type === 'free' && trialEligible && location.pathname !== '/plan')) &&
            !trialBannerDismissed && (
            <div
              style={{
                background: 'linear-gradient(135deg, #7c3aed 0%, #a855f7 50%, #c084fc 100%)',
                padding: '10px 24px',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'space-between',
                flexWrap: 'wrap',
                gap: 12,
              }}
            >
              <div style={{ display: 'flex', alignItems: 'center', gap: 14, flex: 1, minWidth: 0 }}>
                <RocketOutlined style={{ fontSize: 18, color: '#fff' }} />
                <div style={{ display: 'flex', alignItems: 'center', gap: 16, flexWrap: 'wrap' }}>
                  <span style={{ color: '#fff', fontWeight: 700, fontSize: 13 }}>
                    14日間すべての機能を無料でお試しいただけます
                  </span>
                  <span style={{ color: 'rgba(255,255,255,0.75)', fontSize: 12 }}>
                    カード登録のみで即日スタート。期間中のキャンセルで料金は一切かかりません。
                  </span>
                </div>
              </div>
              <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                <Button
                  className="trial-banner-btn"
                  onClick={() => navigate(userProfile ? '/plan' : '/register')}
                  style={{
                    background: '#fff',
                    color: '#7c3aed',
                    border: 'none',
                    fontWeight: 700,
                    fontSize: 12,
                    borderRadius: 6,
                    padding: '4px 16px',
                    height: 'auto',
                    boxShadow: '0 2px 8px rgba(0,0,0,0.15)',
                  }}
                  >
                    {userProfile ? '無料トライアルを始める' : '無料で始める'}
                  </Button>
                <span
                  onClick={() => setTrialBannerDismissed(true)}
                  style={{
                    color: 'rgba(255,255,255,0.6)',
                    cursor: 'pointer',
                    fontSize: 16,
                    lineHeight: 1,
                    padding: '0 4px',
                  }}
                >
                  ×
                </span>
              </div>
            </div>
          )}
          <Content
            style={{
              background: '#fff',
              padding: screens.xl ? '40px 40px 80px' : '16px',
              margin: screens.xl ? '24px 24px 0' : '16px 0 0',
              minHeight: 280,
            }}
          >
            <Routes>
              <Route path="/seo-article" element={<SEOArticleGenerator />} />
              {/* AIOコンテンツモード - ダッシュボード */}
              <Route path="/welcome" element={userProfile ? <Navigate to="/aio/dashboard" replace /> : <WelcomePage />} />
              <Route path="/aio/dashboard" element={userProfile ? <AIODashboard /> : <Navigate to={`/welcome${location.search}`} replace />} />
              <Route
                path="/aio"
                element={<Navigate to={userProfile ? "/aio/dashboard" : `/welcome${location.search}`} replace />}
              />

              {/* ゲーミフィケーション（マイページモード） */}
              <Route path="/rank-system" element={<RankSystemPage />} />
              <Route path="/referral" element={<ReferralPage />} />

              {/* 分析機能 */}
              <Route path="/aio/analysis" element={<AnalysisList />} />
              <Route path="/aio/analysis/new" element={<Analysis />} />
              <Route path="/aio/analysis/:analysisId" element={<Analysis />} />
              <Route path="/aio/analysis/list" element={<AnalysisList />} />
              {/* 生成機能（v2） */}
              <Route
                path="/aio/generation/new"
                element={<AioGenerationWizard />}
              />
              <Route
                path="/aio/generation/:uuid"
                element={<AioGenerationWizard />}
              />
              <Route
                path="/aio/generation/:uuid/step1"
                element={<AioGenerationWizard />}
              />
              <Route
                path="/aio/generation/:uuid/step2"
                element={<AioGenerationWizard />}
              />
              <Route
                path="/aio/generation/:uuid/step3"
                element={<AioGenerationWizard />}
              />
              <Route
                path="/aio/generation/:uuid/step4"
                element={<AioGenerationWizard />}
              />
              <Route
                path="/aio/generation/:uuid/step4-1"
                element={<AioGenerationWizard />}
              />
              <Route
                path="/aio/generation/:uuid/step4-2"
                element={<AioGenerationWizard />}
              />
              <Route
                path="/aio/generation/:uuid/step4-3"
                element={<AioGenerationWizard />}
              />
              <Route
                path="/aio/generation/:uuid/step4-4"
                element={<AioGenerationWizard />}
              />
              <Route
                path="/aio/generation/list"
                element={
                  <GenerationResultList
                    isAdmin={userRole === 'admin' || userRole === 'master'}
                  />
                }
              />
              <Route
                path="/aio/generation"
                element={
                  <GenerationResultList
                    isAdmin={userRole === 'admin' || userRole === 'master'}
                  />
                }
              />

              {/* 管理画面 */}
              <Route
                path="/aio/management/styles"
                element={<StyleManagement />}
              />
              <Route path="/aio/management/styles/new" element={<StyleNew />} />
              <Route
                path="/aio/management/toppings"
                element={<ToppingManager />}
              />
              <Route path="/aio/style" element={<StyleManagement />} />
              <Route path="/aio/style/new" element={<StyleNew />} />
              <Route path="/aio/original" element={<ToppingManager />} />
              <Route
                path="/prompt-management"
                element={<PromptManagement />}
              />

              <Route
                path="/seo-article/observe_task"
                element={<SEOArticleDashboard />}
              />
              <Route
                path="/seo-article/list"
                element={<SEOArticleList userRole={userRole} />}
              />
              <Route
                path="/seo-article/list/:userId"
                element={<SEOArticleList userRole={userRole} />}
              />
              <Route
                path="/seo-article/detail/:uuid"
                element={<SEOArticleDetail />}
              />
              <Route
                path="/seo-article/keywords-recommend"
                element={<RecommendedKeywords />}
              />
              <Route
                path="/fact-check"
                element={
                  userProfile?.early_access?.fact_check ? (
                    <FactCheckNew />
                  ) : (
                    <Navigate to="/seo-article" replace />
                  )
                }
              />
              <Route
                path="/fact-check/tasks"
                element={
                  userProfile?.early_access?.fact_check ? (
                    <FactCheckList />
                  ) : (
                    <Navigate to="/seo-article" replace />
                  )
                }
              />
              <Route
                path="/fact-check/tasks/:userId"
                element={
                  userProfile?.early_access?.fact_check ? (
                    <FactCheckList />
                  ) : (
                    <Navigate to="/seo-article" replace />
                  )
                }
              />
              <Route
                path="/fact-check/result/:task_id"
                element={
                  userProfile?.early_access?.fact_check ? (
                    <FactCheckDetail />
                  ) : (
                    <Navigate to="/seo-article" replace />
                  )
                }
              />
              <Route
                path="/fact-check/new"
                element={
                  userProfile?.early_access?.fact_check ? (
                    <FactCheckNew />
                  ) : (
                    <Navigate to="/seo-article" replace />
                  )
                }
              />
              <Route
                path="/fact-check/new/:draft_id"
                element={
                  userProfile?.early_access?.fact_check ? (
                    <FactCheckNew />
                  ) : (
                    <Navigate to="/seo-article" replace />
                  )
                }
              />
              <Route path="/settings" element={<UserSettings />} />
              <Route path="/mypage" element={<MyPage />} />
              <Route path="/plan" element={<PlanSettings />} />
              <Route path="/bonus-credit" element={<BonusCreditPurchase />} />
              <Route
                path="/subscription-success"
                element={
                  <AuthenticatedRoute>
                    <SubscriptionSuccess />
                  </AuthenticatedRoute>
                }
              />
              <Route
                path="/trial-success"
                element={
                  <AuthenticatedRoute>
                    <SubscriptionSuccess />
                  </AuthenticatedRoute>
                }
              />
              <Route
                path="/subscription-management"
                element={
                  <AuthenticatedRoute>
                    <SubscriptionManagement />
                  </AuthenticatedRoute>
                }
              />
              <Route
                path="/admin/dashboard"
                element={
                  isAuthenticated &&
                  (userRole === 'admin' || userRole === 'master') ? (
                    <AdminDashboardV2 />
                  ) : (
                    <Navigate to="/" />
                  )
                }
              />
              <Route
                path="/admin/dashboard-legacy"
                element={
                  isAuthenticated &&
                  (userRole === 'admin' || userRole === 'master') ? (
                    <AdminDashboard />
                  ) : (
                    <Navigate to="/" />
                  )
                }
              />
              <Route
                path="/admin/user-management"
                element={
                  userRole === 'admin' || userRole === 'master' ? (
                    <UserList />
                  ) : (
                    <Navigate to="/" />
                  )
                }
              />
              <Route
                path="/admin/transactions"
                element={
                  userRole === 'admin' || userRole === 'master' ? (
                    <AdminTransactions />
                  ) : (
                    <Navigate to="/" />
                  )
                }
              />
              <Route
                path="/admin/email-marketing"
                element={
                  userRole === 'admin' || userRole === 'master' ? (
                    <EmailMarketing />
                  ) : (
                    <Navigate to="/" />
                  )
                }
              />
              <Route
                path="/subscription-canceled"
                element={<SubscriptionCanceled />}
              />
              <Route
                path="/settings/confirm-email/:token"
                element={<ConfirmEmail />}
              />
              <Route path="/shared/:uuid" element={<SharedArticle />} />
              <Route
                path="/shared/fact-check/:task_id"
                element={<SharedFactCheck />}
              />
              <Route
                path="/shared/v2-analysis/:analysisId"
                element={<SharedAnalysis />}
              />
              <Route path="*" element={<Navigate to={userProfile ? "/aio/dashboard" : `/welcome${location.search}`} replace />} />
            </Routes>
          </Content>
          <Footer id="footer">
            <Row gutter={[16, 16]} justify={'space-between'}>
              <Col xs={24} xl={24} xxl={16}>
                <Row className="f_menu">
                  <Col xs={24} md={12} lg={5}>
                    <a href="https://magicss.ai" target="_blank">
                      magicssサービスサイト <ExportOutlined />
                    </a>
                  </Col>
                  <Col xs={24} md={12} lg={5}>
                    <a href="/terms-of-service" target="_blank">
                      サービス利用規約
                    </a>
                  </Col>
                  <Col xs={24} md={12} lg={5}>
                    <a href="/privacy-policy" target="_blank">
                      プライバシーポリシー
                    </a>
                  </Col>
                  <Col xs={24} md={12} lg={4}>
                    <a
                      href={`/contact-us${userProfile ? `?magicss_uuid=${userProfile.user_id}` : ''}`}
                      target="_blank"
                      rel="noopener noreferrer"
                    >
                      お問い合わせ <ExportOutlined />
                    </a>
                  </Col>
                </Row>
              </Col>
            </Row>
            <Row gutter={[16, 16]}>
              <Col
                xs={24}
                lg={24}
                xl={24}
                xxl={24}
                style={{ textAlign: 'center' }}
              >
                © 2026{' '}
                <a href="https://oneword.co.jp" target="_blank">
                  ONEWORD Inc.
                </a>
              </Col>
            </Row>
          </Footer>
        </Layout>
      </Layout>
    );
  };

  const renderUnauthenticatedRoutes = () => (
    <Layout className="gate_wrap">
      <Routes>
        <Route path="/login" element={<Auth />} />
        <Route path="/register" element={<Register />} />
        <Route path="/register/thanks" element={<RegisterThanks />} />
        <Route path="/register-email" element={<RegisterEmail />} />
        <Route path="/register-profile/:token" element={<RegisterProfile />} />
        <Route path="/reset-password/:token" element={<ResetPassword />} />
        <Route path="/confirm-email/:token" element={<ConfirmEmail />} />
        <Route
          path="*"
          element={
            <Navigate to="/login" replace state={{ from: location.pathname }} />
          }
        />
      </Routes>
    </Layout>
  );

  const GUEST_ALLOWED_PATHS = [
    '/welcome',
    '/aio/analysis/new',
    '/aio/analysis/list',
    '/aio/analysis/',
    '/aio/generation/new',
    '/aio/generation/list',
    '/aio/generation/',
  ];

  const renderGuestRoutes = () => {
    const authPaths = ['/login', '/register', '/register-email', '/register-profile', '/register/thanks', '/reset-password', '/confirm-email'];
    const isAuthPath = authPaths.some(p => location.pathname.startsWith(p));
    if (isAuthPath) {
      return renderUnauthenticatedRoutes();
    }
    const path = location.pathname;
    const isAllowed = path === '/' || GUEST_ALLOWED_PATHS.some(p =>
      p.endsWith('/') ? path.startsWith(p) : path === p
    );
    if (!isAllowed) {
      return (
        <Routes>
          <Route path="*" element={<Navigate to={`/welcome${location.search}`} replace />} />
        </Routes>
      );
    }
    return renderAuthenticatedRoutes();
  };

  const renderContent = () => {
    if (isLoading) {
      return (
        <div
          style={{
            display: 'flex',
            justifyContent: 'center',
            alignItems: 'center',
            minHeight: '100vh',
          }}
        >
          <Spin size="large" />
        </div>
      );
    }

    return (
      <Routes>
        <Route path="/shared/:uuid" element={<SharedArticle />} />
        <Route
          path="/shared/fact-check/:task_id"
          element={<SharedFactCheck />}
        />
        <Route
          path="/shared/v2-analysis/:analysisId"
          element={<SharedAnalysis />}
        />
        <Route
          path="/terms-of-service"
          element={
            <TermsOfService
              isAuthenticated={isAuthenticated}
              userProfile={userProfile}
            />
          }
        />
        <Route
          path="/privacy-policy"
          element={
            <PrivacyPolicy
              isAuthenticated={isAuthenticated}
              userProfile={userProfile}
            />
          }
        />
        <Route
          path="/contact-us"
          element={
            <ContactUs
              isAuthenticated={isAuthenticated}
              userProfile={userProfile}
            />
          }
        />
        <Route
          path="/reserve-mtg"
          element={
            <ReserveMtg
              isAuthenticated={isAuthenticated}
              userProfile={userProfile}
            />
          }
        />
        <Route
          path="/support"
          element={
            <SupportPage
              isAuthenticated={isAuthenticated}
              userProfile={userProfile}
            />
          }
        />
        {isAuthenticated ? (
          <Route
            path="/*"
            element={
              <AgreementManager>{renderAuthenticatedRoutes()}</AgreementManager>
            }
          />
        ) : (
          <Route path="/*" element={renderGuestRoutes()} />
        )}
      </Routes>
    );
  };

  return (
    <ConfigProvider
      locale={{
        ...jaJP,
        Tour: {
          Next: '次へ',
          Previous: '前へ',
          Finish: '完了',
        },
      }}
    >
      <PushNotificationProvider>
        <>
          <Helmet>
            <title>magicss</title>
            <meta name="robots" content="noindex" />
            <meta property="og:title" content={`magicss`} />
            <meta property="og:type" content="article" />
            <meta property="og:url" content={window.location.href} />
            <meta
              property="og:image"
              content={`${process.env.PUBLIC_URL}/img/ogp.png`}
            />
            <meta name="twitter:card" content="summary_large_image" />
          </Helmet>

          {/* チュートリアルモーダル */}
          {isAuthenticated && showTutorial && (
            <TutorialModal
              visible={showTutorial}
              onComplete={handleTutorialComplete}
              userProfile={userProfile}
            />
          )}

          {renderContent()}
        </>
      </PushNotificationProvider>
    </ConfigProvider>
  );
};

export default App;
